import { useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection, $isRangeSelection, $parseSerializedNode } from 'lexical';
import { $isPageNode } from '../../nodes/PageNode';
import { 
  setGlobalHeaderFooterContent, 
  getGlobalHeaderFooterContent
} from '../../shared/globalHeaderFooterState';

/**
 * Gets the parent PageNode of a node.
 */
function getParentPage(node) {
  let n = node;
  while (n) {
    if ($isPageNode(n)) return n;
    n = n.getParent();
  }
  return null;
}

/**
 * Gets the region type (header/footer/body) a node is in.
 */
function getRegionType(node) {
  let n = node;
  while (n) {
    const type = typeof n.getType === 'function' ? n.getType() : null;
    if (type === 'page_header') return 'header';
    if (type === 'page_footer') return 'footer';
    if (type === 'page_body') return 'body';
    n = n.getParent();
  }
  return null;
}

/**
 * Clears all children from a region node.
 */
function clearRegion(region) {
  if (!region) return;
  const children = region.getChildren();
  for (const child of children) {
    child.remove();
  }
}

/**
 * Recursively serialize a node and all its children.
 * This is needed because node.exportJSON() does NOT automatically include children!
 */
function serializeNodeRecursively(node) {
  const json = node.exportJSON();
  
  // If the node has children (is an ElementNode), serialize them recursively
  if (typeof node.getChildren === 'function') {
    const children = node.getChildren();
    json.children = children.map(child => serializeNodeRecursively(child));
  }
  
  return json;
}

/**
 * Serialize region children to an array of JSON objects.
 * Uses recursive serialization to capture all nested children including TextNodes.
 */
function serializeRegion(region) {
  if (!region) return [];
  const children = region.getChildren();
  
  const result = [];
  for (const child of children) {
    const serialized = serializeNodeRecursively(child);
    result.push(serialized);
  }
  
  return result;
}

/**
 * Restore serialized content to a region.
 */
function restoreToRegion(region, serializedContent) {
  if (!region || !serializedContent || !Array.isArray(serializedContent)) return;
  
  clearRegion(region);
  
  for (const nodeData of serializedContent) {
    try {
      const node = $parseSerializedNode(nodeData);
      region.append(node);
    } catch (e) {
      // Failed to parse node
    }
  }
}

/**
 * Compare two serialized contents.
 */
function contentsMatch(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * HeaderFooterSyncPlugin - Synchronizes headers and footers across all pages.
 * 
 * Key insight: When new pages are created, they have empty headers/footers.
 * We must NOT use new pages as the source of truth - only existing pages with content.
 */
export default function HeaderFooterSyncPlugin() {
  const [editor] = useLexicalComposerContext();
  
  // Track known page keys to detect new pages
  const knownPageKeysRef = useRef(new Set());
  // Track last serialized content to detect REAL content changes
  const lastHeaderContentRef = useRef(null);
  const lastFooterContentRef = useRef(null);
  const isSyncingRef = useRef(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const unregister = editor.registerUpdateListener(({ editorState, tags }) => {
      // Skip our own sync updates
      if (tags?.has('header-footer-sync')) return;
      if (isSyncingRef.current) return;

      let shouldSync = false;
      let newPagesDetected = false;
      let newPageKeys = [];
      let contentChangedOnExistingPage = false;
      let sourcePageKey = null;

      editorState.read(() => {
        const root = $getRoot();
        const pages = root.getChildren().filter($isPageNode);
        
        if (pages.length === 0) return;

        // Detect new pages
        const currentPageKeys = new Set();
        for (const page of pages) {
          const key = page.getKey();
          currentPageKeys.add(key);
          
          if (!knownPageKeysRef.current.has(key)) {
            newPagesDetected = true;
            newPageKeys.push(key);
          }
        }

        // Get selection info
        const selection = $getSelection();
        let editingPageKey = null;
        let editingRegion = null;

        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const editingPage = getParentPage(anchorNode);
          if (editingPage) {
            editingPageKey = editingPage.getKey();
            editingRegion = getRegionType(anchorNode);
          }
        }

        // INITIALIZATION: On first run, capture state from page 0
        // This happens regardless of where the user is editing
        if (!isInitializedRef.current) {
          const firstPage = pages[0];
          const firstHeader = firstPage.getHeader();
          const firstFooter = firstPage.getFooter();
          
          if (firstHeader && firstFooter) {
            setGlobalHeaderFooterContent(firstHeader, firstFooter);
            lastHeaderContentRef.current = serializeRegion(firstHeader);
            lastFooterContentRef.current = serializeRegion(firstFooter);
            knownPageKeysRef.current = currentPageKeys;
            isInitializedRef.current = true;
            
            if (pages.length > 1) {
              shouldSync = true;
            }
          }
          return;
        }

        // CRITICAL: Only update global state when user is actively editing header/footer
        const isEditingHeaderFooter = editingRegion === 'header' || editingRegion === 'footer';
        const isOnExistingPage = editingPageKey && !newPageKeys.includes(editingPageKey);

        // Determine source page for content
        let sourcePage = null;
        
        if (isEditingHeaderFooter && isOnExistingPage) {
          // User is editing header/footer on an existing page - use that page
          for (const page of pages) {
            if (page.getKey() === editingPageKey) {
              sourcePage = page;
              break;
            }
          }
        }

        // If not actively editing header/footer, don't update global state
        // Just check if we need to sync (e.g., new pages added)
        if (!sourcePage) {
          // No active header/footer editing - just handle new pages
          knownPageKeysRef.current = currentPageKeys;
          
          if (newPagesDetected && pages.length > 1) {
            shouldSync = true;
          }
          return;
        }

        sourcePageKey = sourcePage.getKey();
        const sourceHeader = sourcePage.getHeader();
        const sourceFooter = sourcePage.getFooter();

        if (!sourceHeader || !sourceFooter) return;

        // Serialize current content from source page
        const currentHeaderContent = serializeRegion(sourceHeader);
        const currentFooterContent = serializeRegion(sourceFooter);

        // Check if content changed on an EXISTING page (not a new page)
        const headerChanged = !contentsMatch(currentHeaderContent, lastHeaderContentRef.current);
        const footerChanged = !contentsMatch(currentFooterContent, lastFooterContentRef.current);

        if ((headerChanged || footerChanged) && !newPageKeys.includes(sourcePageKey)) {
          // Content changed on an existing page - update global state
          setGlobalHeaderFooterContent(sourceHeader, sourceFooter);
          lastHeaderContentRef.current = currentHeaderContent;
          lastFooterContentRef.current = currentFooterContent;
          lastFooterContentRef.current = currentFooterContent;
          contentChangedOnExistingPage = true;
        }

        // Update known page keys
        knownPageKeysRef.current = currentPageKeys;

        // Decide if sync is needed
        if (pages.length > 1 && (contentChangedOnExistingPage || newPagesDetected)) {
          shouldSync = true;
        }
      });

      // Perform sync if needed
      if (shouldSync) {
        isSyncingRef.current = true;

        editor.update(
          () => {
            const root = $getRoot();
            const pages = root.getChildren().filter($isPageNode);
            
            if (pages.length <= 1) {
              isSyncingRef.current = false;
              return;
            }

            const globalContent = getGlobalHeaderFooterContent();
            
            if (!globalContent.header && !globalContent.footer) {
              isSyncingRef.current = false;
              return;
            }

            // Find the source page (to skip it)
            const sourceKey = sourcePageKey;

            // Apply global content to all pages except the source
            for (const page of pages) {
              // Skip the source page
              if (page.getKey() === sourceKey) continue;
              if (!page.isAttached()) continue;
              
              const header = page.getHeader();
              const footer = page.getFooter();

              // Sync header
              if (header && globalContent.header) {
                const currentContent = serializeRegion(header);
                if (!contentsMatch(currentContent, globalContent.header)) {
                  restoreToRegion(header, globalContent.header);
                }
              }

              // Sync footer
              if (footer && globalContent.footer) {
                const currentContent = serializeRegion(footer);
                if (!contentsMatch(currentContent, globalContent.footer)) {
                  restoreToRegion(footer, globalContent.footer);
                }
              }
            }

            isSyncingRef.current = false;
          },
          { tag: 'header-footer-sync' }
        );
      }
    });

    return unregister;
  }, [editor]);

  return null;
}

/**
 * Checks if selection is in header/footer.
 */
function isSelectionInHeaderFooter() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;

  let node = selection.anchor.getNode();
  while (node) {
    const type = typeof node.getType === 'function' ? node.getType() : null;
    if (type === 'page_header' || type === 'page_footer') {
      return true;
    }
    node = node.getParent();
  }
  return false;
}

/**
 * Gets which region the selection is in.
 */
function getSelectionRegionType() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;

  let node = selection.anchor.getNode();
  while (node) {
    const type = typeof node.getType === 'function' ? node.getType() : null;
    if (type === 'page_header') return 'header';
    if (type === 'page_footer') return 'footer';
    node = node.getParent();
  }
  return null;
}

/**
 * Hook to access header/footer editing state.
 */
export function useHeaderFooterMode() {
  const [editor] = useLexicalComposerContext();
  const [isInHeaderFooter, setIsInHeaderFooter] = useState(false);
  const [regionType, setRegionType] = useState(null);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        setIsInHeaderFooter(isSelectionInHeaderFooter());
        setRegionType(getSelectionRegionType());
      });
    });
  }, [editor]);

  return { isInHeaderFooter, regionType };
}
